import { handlePostApi } from '@/api/crud';
import {
  menuApiEndpoints,
  superAdminVersionApiEndpoints,
  versionApiEndpoints,
} from '@/api/endpoints';
import CustomTree from '@/components/superadmin/tree/Tree';
import { useFetch } from '@/hooks';
import { optionSearchFunction } from '@/utils/common';
import GlobalDivider from '@/utils/divider/GlobalDivider';
import { rules } from '@/utils/validation/FormValidation';
import { SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Layout,
  Row,
  Select,
  Space,
} from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getParentKey } from '.';
import { useVersions, useMenuList, useCreateVersionMenu } from './query';

const { Content } = Layout;

const SuperadminVersionMenuMapping = () => {
  const [menus, setMenus] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [version, setVersion] = useState();
  const [loading, setLoading] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [checkAll, setCheckAll] = useState(false);
  const [filteredPermissions, setFilteredPermissions] = useState([]);

  const [form] = Form.useForm();
  const navigate = useNavigate();

  const { data } = useVersions();

  const onCheck = (nodeKey, checked) => {
    setCheckedKeys((prev) => {
      let newCheckedKeys = [...prev]; // Create a copy of the previous checked keys

      if (checked) {
        if (!newCheckedKeys.includes(nodeKey)) {
          newCheckedKeys.push(nodeKey);
        }
        const parentKey = getParentKey(nodeKey, menus); // Get the closest parent's key
        if (parentKey && !newCheckedKeys.includes(parentKey)) {
          newCheckedKeys.push(parentKey);
        }
      } else {
        newCheckedKeys = newCheckedKeys.filter((key) => key !== nodeKey);
      }

      return newCheckedKeys;
    });
  };

  const getAllKeys = (data) => {
    let keys = [];
    const extractKeys = (items) => {
      items.forEach((item) => {
        keys.push(item.key);
        if (item.children && item.children.length > 0) {
          extractKeys(item.children);
        }
      });
    };
    extractKeys(data);
    return keys;
  };

  const onSelectAll = () => {
    if (checkAll) {
      setCheckedKeys([]);
      setCheckAll(false);
    } else {
      setCheckedKeys(getAllKeys(menus));
      setCheckAll(true);
    }
  };
  const { mutate } = useCreateVersionMenu(form);
  const onFinish = async () => {
    mutate(
      {
        versionId: version,
        menuIds: checkedKeys,
      },
      {
        onSuccess: () => {
          form.resetFields();
          setVersion();
          setSearchTerm('');
          setCheckedKeys([]);
          setCheckAll(false);
          navigate('/superadmin/version/menu/mapping/list/');
        },
      },
    );
  };

  const handleVersionChange = (value) => {
    setVersion(value);
  };

  function generateSubMenuPermissions(subMenus) {
    return subMenus.map((subMenu) => ({
      key: subMenu.referenceId,
      title: subMenu.Name,
      children: subMenu.subMenus
        ? generateSubMenuPermissions(subMenu.subMenus)
        : [],
    }));
  }
  const { data: menuList } = useMenuList();

  useEffect(() => {
    if (menuList?.ResponseCode === '0') {
      const initialPermissions = menuList?.data.menus?.map((menu) => ({
        key: menu.referenceId,
        title: menu.Name,
        children: menu.subMenus
          ? generateSubMenuPermissions(menu.subMenus)
          : [],
      }));
      setMenus(initialPermissions || []);
      setFilteredPermissions(initialPermissions || []);
    }
  }, [menuList]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout>
        <Content style={{ padding: '24px' }}>
          <GlobalDivider name="Version Menu Mapping" />
          <Form form={form} onFinish={onFinish}>
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={12}>
                <Form.Item
                  name="versionId"
                  validateTrigger="onChange"
                  rules={rules.required}
                >
                  <Select
                    placeholder="Select Version"
                    filterOption={optionSearchFunction}
                    options={data?.data?.map((item) => ({
                      value: item.referenceId,
                      label: item?.versionName,
                    }))}
                    onChange={handleVersionChange}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Input
              placeholder="Search menu..."
              className="tw-mb-4"
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />

            <Checkbox onClick={onSelectAll} className="tw-w-full tw-mb-4">
              Select All
            </Checkbox>

            <CustomTree
              treeData={filteredPermissions}
              checkedKeys={checkedKeys}
              onCheck={onCheck}
            />

            <div style={{ textAlign: 'right', marginTop: '12px' }}>
              <Space size="large">
                <Button
                  htmlType="submit"
                  className="submit-button"
                  loading={loading}
                  type="primary"
                >
                  Create
                </Button>
                <Button className="cancel-button" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
              </Space>
            </div>
          </Form>
        </Content>
      </Layout>
    </Layout>
  );
};

export default SuperadminVersionMenuMapping;
